import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { api } from '../lib/api.js';
import { describeWindows, type SendingPolicy } from '../lib/campaigns.js';
import { SendingPolicyEditor } from './SendingPolicyEditor.js';
import { Info } from './Info.js';

/**
 * Sendeprofile: Warmup, Rate-Limits, Sendefenster, Ausnahmetage.
 *
 * Diese Liste zeigt nur; eingestellt wird in SendingPolicyEditor. Die Trennung
 * ist Absicht: der Editor wird ab Phase 3 auch aus dem Kampagnen-Editor heraus
 * aufgeklappt, und ein Formular, das in einer Liste steckt, laesst sich dort
 * nicht wiederverwenden.
 */

export function SendingPolicyList() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SendingPolicy | 'neu' | null>(null);

  const policies = useQuery({
    queryKey: ['sending-policies'],
    queryFn: ({ signal }) => api.get<{ items: SendingPolicy[] }>('/api/sending-policies', signal),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/sending-policies/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sending-policies'] }),
  });

  const items = policies.data?.items ?? [];

  return (
    <section className="card">
      <div className="card__body stack">
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h2 className="check__head" style={{ fontSize: 'var(--text-lg)' }}>
            <span>Sendeprofile</span>
            <Info zu="warmup" />
          </h2>
          <Button variant="secondary" onClick={() => setEditing('neu')}>
            Profil anlegen
          </Button>
        </div>

        {items.length === 0 && !policies.isLoading && (
          <div className="alert" data-tone="warning" role="status">
            Kein Sendeprofil vorhanden. Kampagnen ohne Profil senden{' '}
            <strong>ohne Warmup, ohne Rate-Limit und ohne Sendefenster</strong> — bei einer neuen
            Domain ist das der schnellste Weg in den Spam-Ordner.
          </div>
        )}

        {items.map((policy) => (
          <article
            key={policy.id}
            className="check"
            data-status={policy.sendWindows.length === 0 ? 'warn' : 'ok'}
          >
            <header className="check__head">
              <strong>{policy.name}</strong>
            </header>
            <p className="muted text-sm">{describePolicy(policy)}</p>
            <div className="dialog__actions">
              <Button variant="ghost" onClick={() => setEditing(policy)}>
                Bearbeiten
              </Button>
              <Button variant="danger" onClick={() => remove.mutate(policy.id)}>
                Entfernen
              </Button>
            </div>
          </article>
        ))}

        {editing && (
          <SendingPolicyEditor
            policy={editing === 'neu' ? null : editing}
            onClose={() => setEditing(null)}
          />
        )}
      </div>
    </section>
  );
}

/** Beschreibt das Profil in einem Satz — damit es ohne Aufklappen lesbar ist. */
function describePolicy(policy: SendingPolicy): string {
  const teile: string[] = [];

  teile.push(
    policy.warmupEnabled
      ? `Warmup ab ${policy.warmupStart.toLocaleString('de-DE')} × ${policy.warmupMultiplier} je Sendetag bis ${policy.warmupCap.toLocaleString('de-DE')}`
      : 'kein Warmup',
  );

  teile.push(
    policy.emailsPerHour > 0
      ? `max. ${policy.emailsPerHour.toLocaleString('de-DE')}/Stunde`
      : policy.warmupEnabled
        ? 'Automatisches Stunden-Pacing (~44 bis 294/Std.)'
        : 'kein Stundenlimit',
  );
  teile.push(
    policy.emailsPerDay > 0
      ? `max. ${policy.emailsPerDay.toLocaleString('de-DE')}/Tag`
      : 'kein Tageslimit',
  );

  // Eine leere Fensterliste ist etwas anderes als „ganztags“ — sie bedeutet,
  // dass nie etwas rausgeht. Das muss hier stehen, sonst sucht jemand den
  // Fehler spaeter beim Worker.
  teile.push(describeWindows(policy.sendWindows));
  teile.push(policy.timezone);

  if (policy.blackoutDates.length > 0) {
    teile.push(`${policy.blackoutDates.length} Ausnahmetage`);
  }

  return teile.join(' · ');
}
